import {
  Box,
  Grid
} from '@mui/material';
import { EmptyConversationContent } from 'components/inference/components/empty-conversation-content';
import { MessageContent } from 'components/inference/components/message-content';
import { useAutoscroll } from 'hooks/useAutoscroll';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useStyles } from './styles';
import { useInferenceV2 } from 'hooks/useInferenceV2';
import { PromptInput } from 'components/inference/components/prompt-input';
import { useLLMProxyServiceContext } from 'contexts/llmProxySerivceContext';
import { ImageOverlay } from 'components/image-overlay';

export const Chat = () => {
  const {
    htmlContent,
    isLoadingInference,
    sendQuestion,
    messageList,
    isRecording,
    submitRecordedAudio,
    startRecordAudio,
    playSpeech,
    stopSpeech,
    currentPlaying,
    isSpeechLoading,
    toogleAudioEnable,
  } = useInferenceV2({});
  const {
    prompts,
    promptSelection,
    selectPrompt,
    triggerClearConversation,
    promptLoadState,
    serviceReadiness,
    isServiceReady,
    pendingAssistantMessage,
    consumeAssistantMessage,
  } = useLLMProxyServiceContext();
  const classes = useStyles();
  const { t } = useTranslation();
  const { htmlContainerRef } = useAutoscroll(isLoadingInference);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  useEffect(() => {
    if (pendingAssistantMessage === null || isLoadingInference || !isServiceReady) return;

    // Consume first so the message is sent exactly once, even if sendQuestion's identity changes.
    consumeAssistantMessage();
    sendQuestion(pendingAssistantMessage);
  }, [pendingAssistantMessage, isLoadingInference, isServiceReady, consumeAssistantMessage, sendQuestion]);

  const handleSelectImagePreview = (image: string) => setPreviewImage(image);

  return (
    <Grid
      className={classes.root}
      container
      style={{
        gap: "0px",
        padding: 0
      }}
      direction="column"
      wrap="nowrap"
    >

      <Grid
        item
        xs
        style={{
          minHeight: 0,
          display: "flex",
          flexGrow: 1,
          overflow: "auto",
          padding: 12
        }}
      >
        <div
          ref={htmlContainerRef}
          style={{
            width: '100%',
            overflowX: 'hidden',
            scrollbarWidth: 'thin'
          }}
        >
          {messageList.length === 0 && !isLoadingInference && (
            <EmptyConversationContent textColor="#FFFFFF87" />
          )}
          {messageList.map((message) => (
            <MessageContent
              key={message.id}
              message={message}
              onViewSourceClick={() => { }}
              onPlaySpeech={playSpeech}
              onStopSpeech={stopSpeech}
              isAudioPlaying={currentPlaying === message.id}
              isAudioLoading={isSpeechLoading}
              onViewImagePreview={handleSelectImagePreview}
            />
          ))}
        </div>
      </Grid>

      <Grid item style={{ padding: 12, paddingTop: 0, display: 'flex', alignItems: 'flex-end', width: '100%' }}>
        <PromptInput
          contentIsLoading={isLoadingInference}
          onSendPrompt={sendQuestion}
          isRecording={isRecording}
          submitRecordedAudio={submitRecordedAudio}
          startRecordAudio={startRecordAudio}
          textColor="white"
          toogleAudioEnable={toogleAudioEnable}
          onViewImagePreview={handleSelectImagePreview}
          isAudioActive={Boolean(currentPlaying)}
          onStopAudio={stopSpeech}
          promptSelector={{
            prompts,
            promptSelection,
            selectPrompt,
            triggerClearConversation,
            hasMessages: messageList.length > 0,
            promptLoadState,
            serviceReadiness,
          }}
        />
      </Grid>

      <ImageOverlay
        open={previewImage !== null}
        onClose={() => setPreviewImage(null)}
        base64={previewImage ?? ""}
      />
    </Grid>
  );
};
